import { useMemo } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, Calendar, ChevronRight } from "lucide-react";

/**
 * Properties dashboard widget — flags any property whose stored
 * `taxes_due_date` falls within the next 30 days. Red bar when ≥1 property
 * is in the danger window; hidden entirely when nothing is due.
 *
 * Uses the property list already loaded by the parent, so no extra API
 * call. Computes "days remaining" client-side from the ISO date string.
 */
export default function TaxesDueWidget({ properties = [] }) {
  const { dueSoon, overdue } = useMemo(() => {
    const now = new Date();
    now.setHours(0, 0, 0, 0);
    const cutoff = new Date(now);
    cutoff.setDate(cutoff.getDate() + 30);
    const soon = [];
    const past = [];
    for (const p of properties) {
      if (!p.taxes_due_date) continue;
      const due = new Date(p.taxes_due_date + "T00:00:00");
      if (Number.isNaN(due.getTime())) continue;
      const daysLeft = Math.round((due - now) / (1000 * 60 * 60 * 24));
      const row = { ...p, daysLeft, dueIso: p.taxes_due_date };
      if (daysLeft < 0) past.push(row);
      else if (daysLeft <= 30) soon.push(row);
    }
    soon.sort((a, b) => a.daysLeft - b.daysLeft);
    past.sort((a, b) => a.daysLeft - b.daysLeft); // most overdue first (most negative)
    return { dueSoon: soon, overdue: past };
  }, [properties]);

  const total = dueSoon.length + overdue.length;
  if (total === 0) return null;

  return (
    <div
      className="mb-6 border-2 border-red-300 bg-red-50 rounded-sm p-4"
      data-testid="taxes-due-widget"
    >
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-sm bg-red-600 text-white flex items-center justify-center shrink-0">
          <AlertTriangle className="w-5 h-5" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-baseline gap-2 flex-wrap">
            <h3
              className="text-base font-bold text-red-900 tracking-tight"
              style={{ fontFamily: "Outfit" }}
            >
              Property taxes due soon
            </h3>
            <span
              className="text-xs font-bold uppercase tracking-widest text-red-700"
              data-testid="taxes-due-count"
            >
              {total} property{total === 1 ? "" : "ies"} need attention
            </span>
          </div>
          <p className="text-xs text-red-700 mt-0.5">
            {overdue.length > 0
              ? `${overdue.length} overdue · ${dueSoon.length} due within 30 days. Pay before the county levies penalties.`
              : `${dueSoon.length} property tax payment${dueSoon.length === 1 ? "" : "s"} due in the next 30 days.`}
          </p>

          <ul className="mt-3 divide-y divide-red-200">
            {[...overdue, ...dueSoon].map((p) => (
              <li
                key={p.id}
                className="flex items-center justify-between gap-3 py-2"
                data-testid={`taxes-due-row-${p.id}`}
              >
                <div className="flex items-center gap-2 min-w-0">
                  <Calendar className="w-3.5 h-3.5 text-red-600 shrink-0" />
                  <div className="min-w-0">
                    <div className="font-semibold text-slate-900 truncate text-sm">
                      {p.name}
                    </div>
                    <div className="text-xs text-slate-500 truncate">{p.address}</div>
                  </div>
                </div>
                <div className="flex items-center gap-3 shrink-0">
                  <div className="text-right">
                    <div
                      className={`text-sm font-bold ${
                        p.daysLeft < 0
                          ? "text-red-700"
                          : p.daysLeft <= 7
                          ? "text-red-700"
                          : "text-red-600"
                      }`}
                      data-testid={`taxes-due-days-${p.id}`}
                    >
                      {p.daysLeft < 0
                        ? `${Math.abs(p.daysLeft)} day${Math.abs(p.daysLeft) === 1 ? "" : "s"} overdue`
                        : p.daysLeft === 0
                        ? "Due today"
                        : `${p.daysLeft} day${p.daysLeft === 1 ? "" : "s"} left`}
                    </div>
                    <div className="text-[10px] text-slate-500 font-mono">
                      {p.dueIso}
                      {p.annual_taxes > 0 && (
                        <>
                          {" · "}
                          ${Math.round(p.annual_taxes).toLocaleString()}
                        </>
                      )}
                    </div>
                  </div>
                  <Link
                    to={`/dashboard/properties#${p.id}`}
                    className="text-red-700 hover:text-red-900 shrink-0"
                    title="Open property"
                    data-testid={`taxes-due-link-${p.id}`}
                  >
                    <ChevronRight className="w-4 h-4" />
                  </Link>
                </div>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}
